<template>
  <div class="dashboard-container">
    <div class="dashboard-header">
      <h1>Willkommen {{ user && user.name ? user.name : 'Administrator' }}</h1>
      <div class="header-actions">
        <button class="refresh-button" @click="loadDashboardData" title="Daten aktualisieren">
          <i class="fas fa-sync-alt"></i>
        </button>
      </div>
    </div>

    <p class="description">Verwalten Sie Firmen und Benutzerkonten</p>
    <div class="dashboard-layout">
      
      <div class="dashboard-main">
        <!-- Statistik-Karten -->
        <div class="stats-grid">
          <div class="glass-card primary-card">
            <div class="card-icon">
              <i class="fas fa-building"></i>
            </div>
            <div class="card-content">
              <span class="card-label">Firmen</span>
              <div class="card-value">{{ statistics.totalCompanies }}</div>
              <p class="card-description">Registrierte Unternehmen</p>
            </div>
            <router-link to="/companies" class="card-action">
              <span>Alle anzeigen</span>
              <i class="fas fa-arrow-right"></i>
            </router-link>
          </div>

          <div class="glass-card secondary-card">
            <div class="card-icon">
              <i class="fas fa-tasks"></i>
            </div>
            <div class="card-content">
              <span class="card-label">Tests</span>
              <div class="card-value">{{ statistics.totalTests || 0 }}</div>
              <p class="card-description">Erstellte Tests</p>
            </div>
            <router-link to="/tests" class="card-action">
              <span>Alle Tests anzeigen</span>
              <i class="fas fa-arrow-right"></i>
            </router-link>
          </div>

          <div v-if="statistics.companyWithMostTests" class="glass-card tertiary-card">
            <div class="card-icon">
              <i class="fas fa-trophy"></i>
            </div>
            <div class="card-content">
              <span class="card-label">Aktivste Firma</span>
              <div class="card-value">{{ statistics.companyWithMostTests.name }}</div>
              <p class="card-description">{{ statistics.companyWithMostTests.tests_count }} Tests</p>
            </div>
            <router-link to="/admin/analysis" class="card-action">
              <span>Zur Analyse</span>
              <i class="fas fa-arrow-right"></i>
            </router-link>
          </div>
        </div>

        <!-- Firmenliste -->
        <div class="companies-section">
          <div class="section-header">
            <h2 class="section-title">Aktuelle Firmen</h2>
            <router-link to="/companies" class="view-all">
              Alle Firmen <i class="fas fa-chevron-right"></i>
            </router-link>
          </div>
          
          <div class="companies-grid">
            <div v-if="loading" class="text-center py-5">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Laden...</span>
              </div>
              <p class="mt-2">Firmen werden geladen...</p>
            </div>
            <div v-else-if="error" class="alert alert-danger">
              {{ error }}
            </div>
            <div v-else-if="companies.length === 0" class="empty-state">
              <i class="fas fa-building fa-3x mb-3 text-muted"></i>
              <h3>Keine Firmen vorhanden</h3>
              <p>Sie haben noch keine Firmen angelegt.</p>
              <router-link to="/companies/create" class="btn btn-primary mt-3">
                <i class="fas fa-plus me-2"></i>Firma erstellen
              </router-link>
            </div>
            <template v-else>
              <div v-for="company in latestCompanies" :key="company.id" class="company-card">
                <div class="company-header">
                  <div class="company-logo" v-if="company.logo_url">
                    <img :src="company.logo_url" :alt="company.name">
                  </div>
                  <div class="company-logo" v-else>
                    {{ getCompanyInitials(company.name) }}
                  </div>
                  <div class="company-info">
                    <a :href="`/companies/${company.id}`" class="company-name">{{ company.name }}</a>
                    <br>
                    <a :href="`mailto:${company.email}`" class="company-email">{{ company.email }}</a>
                    <a :href="`tel:${company.phone}`" class="company-contact" v-if="company.phone">
                      <i class="fas fa-phone-alt me-2"></i>{{ company.phone }}
                    </a>
                    <a :href="`https://${company.website}`" class="company-contact" v-if="company.website">
                      <i class="fas fa-globe me-2"></i>{{ company.website }}
                    </a>
                  </div>
                </div>
                
                <div class="company-actions">
                  <button @click="impersonateCompanyAdmin(company)" class="btn btn-sm btn-outline-primary">
                    <i class="fas fa-user-secret me-1"></i>Als Admin anmelden
                  </button>
                  <router-link :to="`/companies/${company.id}/edit`" class="btn btn-sm btn-link">
                    <i class="fas fa-edit me-1"></i>Bearbeiten
                  </router-link>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
      
      <!-- Sidebar mit Schnellzugriff -->
      <div class="dashboard-sidebar">
        <!-- Schnellzugriff -->
        <div class="sidebar-section quick-actions">
          <h2 class="section-title">Schnellzugriff</h2>
          <div class="action-buttons">
            <router-link to="/settings" class="action-button">
              <i class="fas fa-cogs"></i>
              <span>Einstellungen</span>
            </router-link>
            <!-- <router-link to="/admin/analysis" class="action-button">
              <i class="fas fa-chart-pie"></i>
              <span>Statistiken</span>
            </router-link> -->
            <router-link to="/profile" class="action-button">
              <i class="fas fa-user-shield"></i>
              <span>Profil</span>
            </router-link>
            <router-link to="/companies/create" class="action-button">
              <i class="fas fa-plus-circle"></i>
              <span>Firma erstellen</span>
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useAuthStore } from '@/stores/auth';
import { useToast } from 'vue-toastification';
import { useRouter } from 'vue-router';
import adminService from '@/services/adminService.js';

export default {
  name: 'AdminDashboard',
  setup() {
    const authStore = useAuthStore();
    const toast = useToast();
    const router = useRouter();
    
    // State
    const companies = ref([]);
    const loading = ref(false);
    const error = ref(null);
    const statistics = ref({
      totalCompanies: 0,
      totalTests: 0,
      companyWithMostTests: null
    });
    
    // Computed
    const user = computed(() => authStore.user);
    const isSuperAdmin = computed(() => authStore.isSuperAdmin);
    const latestCompanies = computed(() => companies.value.slice(0, 6));
    
    // Methods
    const loadDashboardData = async () => {
      loading.value = true;
      try {
        const response = await adminService.getDashboard();
        statistics.value = response.data.statistics;
        companies.value = response.data.latestCompanies;
      } catch (err) {
        error.value = 'Fehler beim Laden der Dashboard-Daten';
        console.error(err);
      } finally {
        loading.value = false;
      }
    };
    
    const impersonateCompanyAdmin = async (company) => {
      try {
        if (!company || !company.id) {
          toast.error('Ungültige Firma ausgewählt.');
          return;
        }
        
        // Auth-Store-Methode direkt verwenden
        await authStore.impersonateUser(company.id);
        
        router.push('/company/dashboard');
      } catch (err) {
        console.error('Fehler bei der Impersonation:', err);
        toast.error(`Fehler bei der Impersonation: ${err.response?.data?.message || err.message}`);
      }
    };
    
    const getCompanyInitials = (name) => {
      if (!name) return '';
      
      return name
        .split(' ')
        .map(word => word.charAt(0))
        .join('')
        .toUpperCase()
        .slice(0, 2);
    };
    
    // Lifecycle hooks
    onMounted(() => {
      loadDashboardData();
    });
    
    return {
      // State
      companies,
      loading,
      error,
      statistics,
      
      // Computed
      user,
      isSuperAdmin,
      latestCompanies,
      
      // Methods
      loadDashboardData,
      impersonateCompanyAdmin,
      getCompanyInitials
    };
  }
};
</script>

<style lang="scss">
</style>